import React, { useState, useRef, useEffect } from 'react';
import InlineEdit from './InlineEdit';
import clientCandidateDataService from '../services/clientCandidateDataService';

/**
 * EnhancedCandidateHeader
 * - Inline editable: Name, Nickname, Master ID, Internal ID
 * - Profile picture upload on hover with expansion on click
 * - Submission tracking badge with recruiter/admin info and date
 * - Horizontal tags bar with search to add existing tags
 */

const EnhancedCandidateHeader = ({ profile, onRefresh, cardId, pipelineId, isClientUser, inPreviewPanel }) => {
  const [copied, setCopied] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadOverlay, setShowUploadOverlay] = useState(false);
  const [showExpandedPicture, setShowExpandedPicture] = useState(false);
  const [showPhotoHistory, setShowPhotoHistory] = useState(false);
  const [photoHistory, setPhotoHistory] = useState([]);
  const [loadingPhotoHistory, setLoadingPhotoHistory] = useState(false);
  const [showTagSearch, setShowTagSearch] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [tagSearchResults, setTagSearchResults] = useState([]);
  // Keyboard-navigation cursor for the tag search dropdown. Index into
  // the currently-visible tagSearchResults. Reset whenever the results
  // list changes so a fresh search always starts on the first item.
  const [tagHighlightIndex, setTagHighlightIndex] = useState(0);
  const [isSearchingTags, setIsSearchingTags] = useState(false);
  const fileInputRef = useRef(null);
  const tagSearchRef = useRef(null);

  // (Candidate search code now lives in CandidatePreviewPanel's sidebar
  // because it's per-search, not per-candidate.)

  const clientData = profile?.client_data || {};
  const customTags = clientData.custom_tags || {};
  // custom_tags may be stored either as an object-map { "68": {name,color} }
  // OR as an array of single-key wrappers [ {"68":{name,color}} ] (the app's
  // actual stored shape). Normalize both to [key, value] entries so tags
  // always render by name, never as bare index numbers.
  const tagEntries = Array.isArray(customTags)
    ? customTags.flatMap(w => (w && typeof w === 'object' ? Object.entries(w) : []))
    : Object.entries(customTags);

  // Tag colors (same as CustomFieldsWidget)
  const tagColors = [
    { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
    { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
    { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
    { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
    { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-200' },
    { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200' },
    { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
    { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200' }
  ];

  const getTagColor = (key) => {
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
    return tagColors[Math.abs(hash) % tagColors.length];
  };

  // Compensation summary chip — pulls the most useful range/value from clientData.
  // Mirrors the same priority as the CompensationWidget's calculateTotalCompTarget.
  const compSummary = (() => {
    const fmt = (n) => { const v = Number(n); if (!v) return ''; return v >= 1_000_000 ? `$${(v/1_000_000).toFixed(1)}M` : v >= 1000 ? `$${(v/1000).toFixed(0)}K` : `$${v.toLocaleString()}`; };
    const has = (v) => v != null && v !== '' && !isNaN(Number(v)) && Number(v) > 0;
    const range = (min, max) => {
      if (has(min) && has(max)) return Number(min) === Number(max) ? fmt(min) : `${fmt(min)}–${fmt(max)}`;
      if (has(min)) return `${fmt(min)}+`;
      if (has(max)) return `Up to ${fmt(max)}`;
      return null;
    };
    // Prefer expected total, then current total, then expected base, then current base
    let value = range(clientData.expected_total_min, clientData.expected_total_max);
    let label = 'Expected';
    if (!value && has(clientData.expected_total)) { value = fmt(clientData.expected_total); }
    if (!value) {
      value = range(clientData.current_total_min, clientData.current_total_max);
      if (value) label = 'Current';
    }
    if (!value && has(clientData.current_total_comp)) { value = fmt(clientData.current_total_comp); label = 'Current'; }
    if (!value) {
      value = range(clientData.expected_base_min, clientData.expected_base_max);
      if (value) label = 'Expected Base';
    }
    if (!value && has(clientData.current_base_salary)) { value = fmt(clientData.current_base_salary); label = 'Current Base'; }
    return value ? { label, value } : null;
  })();

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleSaveClientName = async (newName) => {
    try {
      setSaving(true);
      await clientCandidateDataService.updateData(profile.id, {
        ...profile.client_data,
        client_name: newName
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating name:', error);
      throw new Error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNickname = async (newNickname) => {
    try {
      setSaving(true);
      await clientCandidateDataService.updateData(profile.id, {
        ...profile.client_data,
        client_nickname: newNickname
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating nickname:', error);
      throw new Error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInternalRef = async (newRef) => {
    try {
      setSaving(true);
      await clientCandidateDataService.updateData(profile.id, {
        ...profile.client_data,
        internal_reference: newRef
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating internal reference:', error);
      throw new Error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Tag management - search and select existing tags
  const [allFirmTags, setAllFirmTags] = useState([]);

  // Load all firm tags once on mount
  useEffect(() => {
    const loadTags = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const isInternal = user.user_type === 'admin' || user.user_type === 'internal';
        const endpoint = isInternal
          ? '/api/candidate-archive/internal-archive/filter-options'
          : '/api/client/archive/filter-options';
        const response = await fetch(endpoint, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAllFirmTags(data.tags || []);
        }
      } catch (error) {
        console.error('Error loading tags:', error);
      }
    };
    loadTags();
  }, []);

  // Filter tags locally based on search query
  useEffect(() => {
    if (!showTagSearch) return;
    const available = allFirmTags.filter(tag => !customTags[String(tag.id)]);
    if (!tagSearchQuery) {
      setTagSearchResults(available);
    } else {
      setTagSearchResults(available.filter(tag =>
        tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase())
      ));
    }
  }, [tagSearchQuery, showTagSearch, allFirmTags, customTags]);

  // Auto-refresh tags when the AI assistant applies or removes one
  // for THIS candidate. The launcher fires a window CustomEvent on
  // every successful write tool_result; we filter by tool +
  // candidate_id and re-fetch the profile so the tag list stays in
  // sync. Both add and remove route here — the header re-renders the
  // same pill list either way.
  useEffect(() => {
    if (!profile?.id) return;
    const TAG_TOOLS = new Set(['tag_candidate', 'remove_tag']);
    const onAssistantWrite = (e) => {
      const d = e.detail || {};
      if (TAG_TOOLS.has(d.tool) && Number(d.input?.candidate_id) === Number(profile.id)) {
        if (onRefresh) onRefresh();
      }
    };
    window.addEventListener('banyak:assistant_write', onAssistantWrite);
    return () => window.removeEventListener('banyak:assistant_write', onAssistantWrite);
  }, [profile?.id, onRefresh]);

  const handleTagSearchClick = () => {
    setShowTagSearch(true);
    setTagSearchQuery('');
    // Show all available tags immediately
    const available = allFirmTags.filter(tag => !customTags[String(tag.id)]);
    setTagSearchResults(available);
    setTimeout(() => tagSearchRef.current?.focus(), 100);
  };

  // Listen for the "t" hotkey dispatched by CandidatePreviewPanel so
  // recruiters can jump into tag search without reaching for the mouse.
  // Same effect as clicking the "Add Tag" button.
  useEffect(() => {
    const handler = () => handleTagSearchClick();
    window.addEventListener('banyak:focus_tag_search', handler);
    return () => window.removeEventListener('banyak:focus_tag_search', handler);
  }, [allFirmTags, customTags]);

  // Listen for the "toggle clean" event dispatched by CandidatePreviewPanel
  // when the user presses 'c'. Same pattern as banyak:focus_tag_search
  // above — avoids threading a ref up. The `saving` guard prevents rapid-
  // fire toggling from double-firing while the previous request is in
  // flight (belt-and-braces — handleToggleClean also sets saving=true).
  useEffect(() => {
    const handler = () => { if (!saving) handleToggleClean(); };
    window.addEventListener('banyak:toggle_clean', handler);
    return () => window.removeEventListener('banyak:toggle_clean', handler);
  }, [saving, clientData, profile]);

  const handleAddTagFromSearch = async (tag) => {
    try {
      setSaving(true);
      const tagId = String(tag.id);
      // Use tag ID as key with {name, color} as value for proper filtering
      const newTags = { 
        ...customTags, 
        [tagId]: { name: tag.name, color: tag.color || '#6B7280' } 
      };
      await clientCandidateDataService.updateData(profile.id, {
        ...clientData,
        custom_tags: newTags
      });
      setShowTagSearch(false);
      setTagSearchQuery('');
      setTagSearchResults([]);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error adding tag:', error);
      alert('Failed to add tag');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTag = async (key) => {
    try {
      setSaving(true);
      // Preserve whichever shape is stored: array-of-wrappers or object-map.
      const newTags = Array.isArray(customTags)
        ? customTags.filter(w => !(w && typeof w === 'object' && Object.prototype.hasOwnProperty.call(w, key)))
        : (() => { const n = { ...customTags }; delete n[key]; return n; })();
      await clientCandidateDataService.updateData(profile.id, {
        ...clientData,
        custom_tags: newTags
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting tag:', error);
      alert('Failed to delete tag');
    } finally {
      setSaving(false);
    }
  };

  const handleTagSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowTagSearch(false);
      setTagSearchQuery('');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setTagHighlightIndex(i => Math.min(i + 1, Math.max(0, tagSearchResults.length - 1)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setTagHighlightIndex(i => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const tag = tagSearchResults[tagHighlightIndex];
      if (tag) handleAddTagFromSearch(tag);
      return;
    }
  };

  // Reset the keyboard cursor to the top whenever the visible results
  // change (i.e., the user typed something new). Also scroll the newly
  // highlighted row into view so arrow-keying past the bottom of the
  // dropdown reveals more items.
  useEffect(() => { setTagHighlightIndex(0); }, [tagSearchResults]);
  useEffect(() => {
    if (!showTagSearch) return;
    const el = document.querySelector(`[data-tag-index="${tagHighlightIndex}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [tagHighlightIndex, showTagSearch]);

  // Clean status toggle
  const handleToggleClean = async () => {
    try {
      setSaving(true);
      const newCleanStatus = !clientData.is_clean;
      await clientCandidateDataService.updateData(profile.id, {
        ...clientData,
        is_clean: newCleanStatus,
        cleaned_at: newCleanStatus ? new Date().toISOString() : null
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error toggling clean status:', error);
      alert('Failed to update clean status');
    } finally {
      setSaving(false);
    }
  };

  // Format cleaned date
  const formatCleanedDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleProfilePictureClick = (e) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handlePictureExpand = () => {
    if (profilePicture) {
      setShowExpandedPicture(true);
    }
  };

  const handleShowPhotoHistory = async (e) => {
    e.stopPropagation();
    setShowPhotoHistory(true);
    setLoadingPhotoHistory(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/candidates/${profile.id}/photo-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPhotoHistory(data.photos || []);
    } catch (err) {
      console.error('Failed to load photo history:', err);
      setPhotoHistory([]);
    } finally {
      setLoadingPhotoHistory(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('profile_picture', file);

      const response = await fetch(`/api/client/candidates/${profile.id}/profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData
      });

      if (response.ok) {
        if (onRefresh) onRefresh();
      } else {
        const err = await response.json();
        throw new Error(err.error || 'Failed to upload');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      alert('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  // Format submission date as MM/DD/YYYY
  const formatSubmissionDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US'); // MM/DD/YYYY format
  };

  // Get display values - prioritize widget data over base profile
  // (clientData already declared above for tags)
  
  // Name (from client data or base profile)
  const displayName = clientData.client_name || profile.name;
  const displayNickname = clientData.client_nickname;
  // internal_reference is now displayed in CandidatePreviewPanel's small header bar.
  const profilePicture = clientData?.profile_picture_url || profile.profile_picture_url || profile.photo_url || profile.avatar_url || null;
  const [picError, setPicError] = useState(false);
  const validPicture = profilePicture && !picError;

  // Current Position - from positions widget (current_employment) or fallback to base profile
  const currentEmployment = profile.current_employment;
  const displayTitle = currentEmployment?.job_title || clientData.client_current_title || profile.current_title;
  const displayCompany = currentEmployment?.company_name || clientData.client_current_company || profile.current_company;

  // Location - from locations array (primary) or fallback
  const locations = clientData.locations || [];
  const primaryLocation = locations.find(l => l.is_primary) || locations[0];
  const displayLocation = primaryLocation 
    ? [primaryLocation.city, primaryLocation.state, primaryLocation.country !== 'USA' ? primaryLocation.country : null].filter(Boolean).join(', ')
    : (clientData.client_location || profile.location);

  // Email - preferred first, then first non-DNC, then fallback
  const emails = clientData.emails || [];
  const preferredEmail = emails.find(e => e.preferred) || emails.find(e => e.type !== 'Do Not Contact') || emails[0];
  const displayEmail = preferredEmail?.value || clientData.client_email || profile.email;

  // Phone - preferred first, then first non-DNC, then fallback  
  const phones = clientData.phones || [];
  const preferredPhone = phones.find(p => p.preferred) || phones.find(p => p.type !== 'Do Not Contact') || phones[0];
  const displayPhone = preferredPhone?.value || clientData.client_phone || profile.phone;

  return (
    <>
      {/* Photo History Modal */}
      {showPhotoHistory && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPhotoHistory(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-3xl max-h-[85vh] w-full overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Photo History</h3>
                <p className="text-xs text-gray-500 mt-0.5">Previous profile photos from LinkedIn syncs</p>
              </div>
              <button
                onClick={() => setShowPhotoHistory(false)}
                className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingPhotoHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                </div>
              ) : photoHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">No photo history yet</p>
                  <p className="text-xs mt-1">Photos from LinkedIn syncs will appear here when they change</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {photoHistory.map((photo, i) => (
                    <div key={i} className="relative group">
                      <div className={`aspect-square rounded-lg overflow-hidden border-2 ${photo.current ? 'border-emerald-500' : 'border-gray-200'}`}>
                        <img
                          src={photo.url}
                          alt={photo.current ? 'Current photo' : `Previous photo ${i}`}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <div className="mt-1.5 text-center">
                        {photo.current ? (
                          <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Current</span>
                        ) : (
                          <span className="text-[10px] text-gray-500">
                            Replaced {photo.replaced_at ? new Date(photo.replaced_at).toLocaleDateString() : 'earlier'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Expanded Picture Modal */}
      {showExpandedPicture && validPicture && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center cursor-pointer"
          onClick={() => setShowExpandedPicture(false)}
        >
          <div className="relative max-w-lg max-h-[80vh]">
            <img
              src={profilePicture} onError={() => setPicError(true)}
              alt={displayName}
              className="max-w-full max-h-[80vh] rounded-lg shadow-2xl object-contain"
            />
            <button
              onClick={() => setShowExpandedPicture(false)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-900"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className={`group/header border-b shadow-sm transition-colors duration-300 ${
        clientData.is_clean
          ? 'bg-gradient-to-r from-green-50 to-white'
          : 'bg-gradient-to-r from-amber-50/60 to-white'
      }`}>
        <div className="px-4 py-3">
          <div className="flex items-center">
            {/* Left: Profile info */}
            <div className="flex items-center gap-4">

              {/* Profile Picture with Upload and Expand */}
              <div
                className="relative group cursor-pointer"
                onMouseEnter={() => setShowUploadOverlay(true)}
                onMouseLeave={() => setShowUploadOverlay(false)}
                onClick={handlePictureExpand}
              >
                {validPicture ? (
                  <img
                    src={profilePicture} onError={() => setPicError(true)}
                    alt={displayName}
                    className="w-20 h-20 rounded-xl object-cover border border-gray-200 shadow-sm transition-transform group-hover:scale-105"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white text-xl font-bold border border-gray-200 shadow-sm transition-transform group-hover:scale-105">
                    {displayName?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                )}

                {/* Expand hint when hovering over a real photo — render BEFORE
                    the action buttons so the buttons stack above the overlay. */}
                {validPicture && showUploadOverlay && (
                  <div className="absolute inset-0 rounded-xl bg-black bg-opacity-30 flex items-center justify-center pointer-events-none">
                    <svg className="w-7 h-7 text-white opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                )}

                {/* Action badges — sit on the bottom border of the picture, centered, as a horizontal pill row.
                    z-10 keeps them clickable above the overlay above. */}
                {showUploadOverlay && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 -bottom-2 z-10 flex items-center gap-0.5 bg-white rounded-full shadow-md border border-gray-200 px-0.5 py-0.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={handleProfilePictureClick}
                      className="w-6 h-6 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center transition-colors"
                      title="Upload new photo"
                    >
                      {uploading ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                      ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={handleShowPhotoHistory}
                      className="w-6 h-6 rounded-full bg-gray-700 hover:bg-gray-800 text-white flex items-center justify-center transition-colors"
                      title="View photo history"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />

                {/* Submission Tracking Badge - top-right corner of the square */}
                {!inPreviewPanel && profile.source === 'presented' && profile.presented_by_name && (
                  <div
                    className="absolute -top-1.5 -right-1.5 z-10 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center cursor-help shadow"
                    title={`Submitted by ${profile.presented_by_name} on ${formatSubmissionDate(profile.presented_at)}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </div>
                )}
                {!inPreviewPanel && profile.source === 'client_created' && (
                  <div
                    className="absolute -top-1.5 -right-1.5 z-10 w-5 h-5 bg-purple-500 rounded-full border-2 border-white flex items-center justify-center cursor-help shadow"
                    title="Added by your organization"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Name & Info */}
              <div className="flex flex-col gap-0.5">
                {/* Row 1: Full Name + Nickname (both inline-editable) */}
                <div className="flex items-baseline gap-2 flex-wrap">
                  <NameEditor
                    value={displayName}
                    onSave={handleSaveClientName}
                    className="text-lg font-bold text-gray-900"
                    placeholder="Full Name"
                  />
                  <NameEditor
                    value={displayNickname}
                    onSave={handleSaveNickname}
                    className="text-sm text-gray-500 italic"
                    placeholder="nickname"
                    prefix={'"'}
                    suffix={'"'}
                    emptyText="+ nickname"
                  />
                </div>

                {/* Row 2: Title at Company + Submission */}
                {(displayTitle || displayCompany) && (
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <span>
                      {displayTitle && <span className="font-medium text-gray-700">{displayTitle}</span>}
                      {displayTitle && displayCompany && <span className="text-gray-500"> at </span>}
                      {displayCompany && <span className="text-gray-500">{displayCompany}</span>}
                    </span>
                    {profile.source === 'presented' && profile.presented_by_name && (
                      <>
                        <span className="text-gray-300 mx-1">·</span>
                        <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded text-xs">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          <span className="font-medium">{profile.presented_by_name}</span>
                          {profile.presented_at && <span className="text-emerald-500">{formatSubmissionDate(profile.presented_at)}</span>}
                        </span>
                      </>
                    )}
                  </div>
                )}

                {/* Row 3: Contact details — email, phone, location.
                    Style: small icon-tile chips with subtle backgrounds, each one
                    interactive. Color-coded so they're visually distinct from tags. */}
                {(displayEmail || displayPhone || displayLocation) && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                    {displayEmail && (
                      <button
                        onClick={() => copyToClipboard(displayEmail, 'email')}
                        className="flex items-center gap-1.5 pl-1.5 pr-2 py-0.5 rounded-md bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-200 transition-colors group/contact"
                        title="Click to copy email"
                      >
                        <span className="w-4 h-4 rounded bg-emerald-100 text-emerald-700 group-hover/contact:bg-emerald-500 group-hover/contact:text-white flex items-center justify-center transition-colors">
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </span>
                        <span className="text-xs text-gray-700 group-hover/contact:text-emerald-700 transition-colors">{displayEmail}</span>
                        {copied === 'email' && <span className="text-green-600 text-[10px]">✓</span>}
                      </button>
                    )}
                    {displayPhone && (
                      <button
                        onClick={() => copyToClipboard(displayPhone, 'phone')}
                        className="flex items-center gap-1.5 pl-1.5 pr-2 py-0.5 rounded-md bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-200 transition-colors group/contact"
                        title="Click to copy phone"
                      >
                        <span className="w-4 h-4 rounded bg-emerald-100 text-emerald-600 group-hover/contact:bg-emerald-500 group-hover/contact:text-white flex items-center justify-center transition-colors">
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </span>
                        <span className="text-xs text-gray-700 group-hover/contact:text-emerald-700 transition-colors">{displayPhone}</span>
                        {copied === 'phone' && <span className="text-green-600 text-[10px]">✓</span>}
                      </button>
                    )}
                    {displayLocation && (
                      <span className="flex items-center gap-1.5 pl-1.5 pr-2 py-0.5 rounded-md bg-slate-50 border border-slate-200">
                        <span className="w-4 h-4 rounded bg-rose-100 text-rose-600 flex items-center justify-center">
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </span>
                        <span className="text-xs text-gray-700">{displayLocation}</span>
                      </span>
                    )}
                    {/* Compensation chip — same chip language as email/phone/location.
                        Click scrolls to the compensation widget. */}
                    {compSummary && (
                      <button
                        type="button"
                        onClick={() => {
                          const el = document.querySelector('[data-widget="compensation"]')
                            || Array.from(document.querySelectorAll('h2, h3, h4')).find(h => /compensation/i.test(h.textContent || ''));
                          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }}
                        className="flex items-center gap-1.5 pl-1.5 pr-2 py-0.5 rounded-md bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-200 transition-colors group/contact"
                        title={`${compSummary.label} compensation \u00b7 click to view`}
                      >
                        <span className="w-4 h-4 rounded bg-emerald-100 text-emerald-600 group-hover/contact:bg-emerald-500 group-hover/contact:text-white flex items-center justify-center transition-colors">
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </span>
                        <span className="text-[9px] uppercase tracking-wider text-gray-500 group-hover/contact:text-emerald-600 transition-colors">{compSummary.label}</span>
                        <span className="text-xs text-gray-700 group-hover/contact:text-emerald-700 transition-colors font-medium">{compSummary.value}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tags Bar */}
        <div className="px-4 pb-2 flex items-center gap-2">
          {/* Clean status toggle — moved here from CandidatePreviewPanel.
              Sits with the tag row because it's a status/annotation
              action (like tags). Colored bg signals state at a glance:
              green = clean, orange = needs review. Pressing 'c' with
              the preview panel open also toggles this via the
              banyak:toggle_clean event listener above. */}
          <button
            onClick={handleToggleClean}
            disabled={saving}
            title={clientData.is_clean ? 'Mark as needs review (c)' : 'Mark as clean (c)'}
            className={`w-6 h-6 rounded flex items-center justify-center transition-colors flex-shrink-0 disabled:opacity-50 ${
              clientData.is_clean
                ? 'text-green-700 bg-green-100 hover:bg-green-200'
                : 'text-orange-700 bg-orange-100 hover:bg-orange-200'
            }`}
          >
            {clientData.is_clean ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
          </button>

          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          
          <div className="flex flex-wrap items-center gap-1.5">
            {tagEntries.map(([key, value]) => {
              // Handle both old format (string value) and new format (object with name/color)
              const isNewFormat = typeof value === 'object' && value !== null;
              const tagName = isNewFormat ? (value.name || key) : key;
              const tagColor = isNewFormat && value.color ? {
                bg: 'bg-opacity-20',
                text: '',
                border: 'border-opacity-30'
              } : getTagColor(key);
              const customColorStyle = isNewFormat && value.color ? {
                backgroundColor: `${value.color}20`,
                color: value.color,
                borderColor: `${value.color}50`
              } : {};
              const tagValue = isNewFormat ? null : value;
              
              return (
                <div 
                  key={key}
                  className={`tag-color flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${!isNewFormat ? `${tagColor.bg} ${tagColor.text} ${tagColor.border}` : ''}`}
                  style={customColorStyle}
                >
                  <span>{tagName}</span>
                  {tagValue && <span className="opacity-70">: {tagValue}</span>}
                  <button
                    onClick={() => handleDeleteTag(key)}
                    className="ml-0.5 opacity-60 hover:opacity-100 hover:text-red-600 transition-opacity"
                    title="Remove tag"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}

            {/* Add Tag Search */}
            {showTagSearch ? (
              <div className="relative">
                <div className="flex items-center gap-1 bg-white border rounded-full px-2 py-0.5 shadow-sm">
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    ref={tagSearchRef}
                    type="text"
                    value={tagSearchQuery}
                    onChange={(e) => setTagSearchQuery(e.target.value)}
                    onKeyDown={handleTagSearchKeyDown}
                    placeholder="Search tags..."
                    className="w-24 text-xs border-none focus:outline-none focus:ring-0 bg-transparent"
                  />
                  {isSearchingTags && (
                    <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent"></div>
                  )}
                  <button
                    onClick={() => { setShowTagSearch(false); setTagSearchQuery(''); }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Search Results Dropdown */}
                {tagSearchResults.length > 0 && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white border rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                    {tagSearchResults.map((tag, idx) => {
                      const color = getTagColor(tag.name);
                      const isHighlighted = idx === tagHighlightIndex;
                      return (
                        <button
                          key={tag.id}
                          data-tag-index={idx}
                          onClick={() => handleAddTagFromSearch(tag)}
                          onMouseEnter={() => setTagHighlightIndex(idx)}
                          className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${isHighlighted ? 'bg-emerald-50 text-emerald-900' : 'hover:bg-gray-50'}`}
                        >
                          <span className={`w-2 h-2 rounded-full ${color.bg}`}></span>
                          <span>{tag.name}</span>
                          {tag.usage_count > 0 && (
                            <span className="text-xs text-gray-400 ml-auto">{tag.usage_count}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {tagSearchQuery && tagSearchResults.length === 0 && !isSearchingTags && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white border rounded-lg shadow-lg z-50 p-3 text-xs text-gray-500">
                    No tags found. <a href="/client/tags" className="text-emerald-700 hover:underline">Create new tags</a>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleTagSearchClick}
                className="flex items-center gap-1 px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded-full hover:border-gray-400 transition-colors"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Tag
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// NameEditor — plain-text feel for the candidate name/nickname.
// Display: just text. Click anywhere on the text to enter edit mode.
// Edit mode uses a transparent input with a thin blue underline so it doesn't
// change the row height (no padding, matched line-height).
// Enter or blur commits; Escape reverts. When `value` is empty, the placeholder
// text shows as a subtle "+ X" affordance that's still clickable to start editing.
const NameEditor = ({ value, onSave, className = '', placeholder = '', prefix = '', suffix = '', emptyText }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value || '');
  const inputRef = React.useRef(null);

  React.useEffect(() => { setDraft(value || ''); }, [value]);
  React.useEffect(() => {
    if (isEditing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [isEditing]);

  const commit = async () => {
    if (draft === (value || '')) { setIsEditing(false); return; }
    try { await onSave(draft); } catch (e) { console.error(e); }
    setIsEditing(false);
  };
  const cancel = () => { setDraft(value || ''); setIsEditing(false); };

  // Shared styling so the box height matches in both modes (no layout shift)
  const sharedBox = 'inline-block align-baseline leading-tight';

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
        placeholder={placeholder}
        className={`${className} ${sharedBox} bg-transparent border-0 border-b border-emerald-400 focus:outline-none focus:border-emerald-600 px-0 py-0 m-0`}
        style={{ width: `${Math.max((draft || '').length + 2, (placeholder || '').length + 2, 8)}ch`, boxSizing: 'content-box' }}
      />
    );
  }

  const display = value
    ? `${prefix}${value}${suffix}`
    : (emptyText || placeholder);

  return (
    <span
      onClick={() => setIsEditing(true)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsEditing(true); } }}
      className={`${className} ${sharedBox} cursor-text hover:text-emerald-700 transition-colors border-0 border-b border-transparent hover:border-emerald-200 ${!value ? 'text-gray-400 italic' : ''}`}
      title="Click to edit"
    >
      {display}
    </span>
  );
};

// RefEditor was moved into CandidatePreviewPanel's small header bar.

export default EnhancedCandidateHeader;
